import { z } from "zod";

/**
 * The buyer's money doors, **one contract for the doors and for client-web** (`GENERIC-DOOR-SWEEP`, Arc 2, A48):
 * `GET /me/dossiers/{id}/fees`, the three `…/fees/verification` doors and `GET /me/payments/{id}`.
 *
 * Until these files client-web declared an inline type per call and the API answered `Promise<unknown>`. **Money is whole
 * FCFA** (`INTEGER` columns, read as numbers): every `*Amount` below is an integer, and the integration spec that parses
 * the real answer fails on a float or a string. Two shares are never merged into a total (invariant I2): the client's and
 * the seller's travel as `clientAmount` and `vendorAmount`.
 */

/** The fee as a QUOTE, before a request exists: no id, no settlement state; or, with no price published, no amounts and the reason. */
const quotedFeeSchema = z.union([
  z.strictObject({
    quoted: z.literal(true),
    clientAmount: z.number().int(),
    vendorAmount: z.number().int(),
    reducedFlatApplied: z.boolean(),
    basePriceAmount: z.number().int(),
  }),
  z.strictObject({
    quoted: z.literal(true),
    clientAmount: z.null(),
    vendorAmount: z.null(),
    reducedFlatApplied: z.literal(false),
    /** The door's sentence for « no price published »: printed as it stands, never a zero. */
    unpublished: z.string(),
  }),
]);

/** The fee once the buyer has asked to pay: a row with an id and a settlement state per share. */
const mintedFeeSchema = z.strictObject({
  quoted: z.literal(false),
  id: z.string(),
  clientAmount: z.number().int(),
  clientStatus: z.string(),
  vendorAmount: z.number().int(),
  vendorStatus: z.string(),
  reducedFlatApplied: z.boolean(),
  basePriceAmount: z.number().int(),
});

/** The file fees inside the mandate: Hakili's share and the bank's, from the base they were taken from; `null` on a cash file. */
const fileFeesSchema = z.strictObject({
  hakiliAmount: z.number().int(),
  bankAmount: z.number().int(),
  baseAmount: z.number().int(),
  settingsVersion: z.number().int(),
});

/** One means, offered or greyed with its reason (`R81`: absent tells nothing, present and dead tells something false). */
const meanSchema = z.strictObject({
  code: z.string(),
  available: z.boolean(),
  reason: z.string().nullable(),
});

const bankAccountSchema = z.strictObject({
  bankName: z.string(),
  accountName: z.string(),
  accountNumber: z.string(),
});

/** The ACTIVE declaration: the dossier's latest request still waiting, or `null`. */
const declarationSchema = z.strictObject({
  paymentRequestId: z.string(),
  method: z.string(),
  amount: z.number().int(),
  reference: z.string(),
  status: z.string(),
  declaredAt: z.string().nullable(),
  clientReference: z.string().nullable(),
});

/** `GET /api/v1/me/dossiers/{id}/fees`. */
export const buyerFeesAnswerSchema = z.strictObject({
  fee: z.union([quotedFeeSchema, mintedFeeSchema]),
  fileFees: fileFeesSchema.nullable(),
  intention: z.string(),
  means: z.array(meanSchema),
  hakiliAccount: bankAccountSchema.nullable(),
  /** One destination per method, answered only while the method is offered. */
  destinations: z.strictObject({
    orange: z.strictObject({ number: z.string() }).nullable(),
    moov: z.strictObject({ number: z.string() }).nullable(),
    virement: bankAccountSchema.nullable(),
  }),
  declaration: declarationSchema.nullable(),
  transferConfirmationHours: z.number(),
});

/** `POST /api/v1/me/dossiers/{id}/fees/verification`: the payment asked for. */
export const verificationRequestedAnswerSchema = z.strictObject({
  paymentRequestId: z.string(),
  reference: z.string(),
  clientAmount: z.number().int(),
  status: z.string(),
});

/** `POST …/fees/verification/cancel`. */
export const cancelDeclarationAnswerSchema = z.strictObject({ cancelled: z.literal(true) });

/** `POST …/fees/verification/declare-paid`: the instant he said he paid; the clock started once. */
export const declarePaidAnswerSchema = z.strictObject({ declaredAt: z.string() });

/** `GET /api/v1/me/payments/{id}`: one payment request of this client's. */
export const paymentRequestAnswerSchema = z.strictObject({
  payment: z.strictObject({
    id: z.string(),
    amount: z.number().int(),
    method: z.string(),
    status: z.string(),
    reference: z.string(),
    settled_at: z.string().nullable(),
  }),
});

export type BuyerFeesAnswer = z.infer<typeof buyerFeesAnswerSchema>;
export type VerificationRequestedAnswer = z.infer<typeof verificationRequestedAnswerSchema>;
export type CancelDeclarationAnswer = z.infer<typeof cancelDeclarationAnswerSchema>;
export type DeclarePaidAnswer = z.infer<typeof declarePaidAnswerSchema>;
export type PaymentRequestAnswer = z.infer<typeof paymentRequestAnswerSchema>;
